// Competitor intelligence tile + side panel — FE-SCRM-058
// (BE-SCRM-012).
//
// Table of competitor brokers ordered by encounters desc. Click a
// row → side panel listing the underlying opportunities where we
// competed against this firm (closed_won + closed_lost), matched
// client-side against the normalized firm name on each opportunity.
//
// We deliberately don't pre-fetch the closed-opps feed; only the
// first click flips the ``useClosedOpportunitiesQuery`` enabled flag.

import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Badge, Card, Table, TBody, THead, Th, Td, Tr } from "@/design-system";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import {
  useClosedOpportunitiesQuery,
  useCompetitorsQuery,
} from "./hooks";
import {
  STAGE_LABEL, formatCents, formatPercent, normalizeCompanyName,
} from "./labels";
import type {
  CompetitorOpportunityRow, CompetitorRow, ReportPeriod,
} from "./types";


export function CompetitorIntelligenceTile({
  period,
}: { period: ReportPeriod }) {
  const competitors = useCompetitorsQuery(period);
  const [selected, setSelected] = useState<CompetitorRow | null>(null);
  const closedOpps = useClosedOpportunitiesQuery(selected !== null);

  return (
    <Card
      data-testid="competitor-intelligence-tile"
      data-section="competitor-intelligence"
      className="flex flex-col gap-3"
    >
      <header>
        <h3 className="text-md font-semibold text-text-primary">
          Competitor intelligence
        </h3>
        <p className="mt-0.5 text-xs text-text-secondary">
          Which incumbent brokers you face most — and where you tend
          to win or lose against each.
        </p>
      </header>

      {competitors.isLoading ? (
        <DashboardLoadingState variant="list" rows={3} />
      ) : competitors.isError ? (
        <DashboardErrorState
          title="Couldn't load competitor intelligence"
          retry={() => competitors.refetch()}
        />
      ) : competitors.data && competitors.data.rows.length === 0 ? (
        <div data-testid="competitor-empty-state">
          <DashboardEmptyState
            title="No competitor encounters yet"
            description={
              "Add an incumbent broker on a BOR-takeover Opportunity, "
              + "or close a pursuit against a named broker, and the firm "
              + "will land here."
            }
          />
        </div>
      ) : (
        <CompetitorTable
          rows={competitors.data?.rows ?? []}
          selected={selected}
          onSelect={setSelected}
        />
      )}

      {selected && (
        <CompetitorSidePanel
          competitor={selected}
          opportunities={closedOpps.data?.rows ?? []}
          isLoading={closedOpps.isLoading}
          isError={closedOpps.isError}
          onClose={() => setSelected(null)}
        />
      )}
    </Card>
  );
}


function CompetitorTable({
  rows, selected, onSelect,
}: {
  rows: CompetitorRow[];
  selected: CompetitorRow | null;
  onSelect: (row: CompetitorRow) => void;
}) {
  return (
    <div
      data-testid="competitor-table"
      className="overflow-hidden rounded-md border border-border-subtle"
    >
      <Table density="xs" cellPadding="tight">
        <THead className="bg-recessed text-text-tertiary">
          <Tr>
            <Th>Broker</Th>
            <Th>Encounters</Th>
            <Th>Wins against</Th>
            <Th>Losses to</Th>
            <Th>Win rate</Th>
          </Tr>
        </THead>
        <TBody>
          {rows.map((row) => {
            const active = selected?.competitor_id === row.competitor_id;
            return (
              <Tr
                key={row.competitor_id}
                data-testid={`competitor-row-${row.competitor_id}`}
                onClick={() => onSelect(row)}
                aria-selected={active}
                className={[
                  "cursor-pointer border-t border-border-subtle",
                  active
                    ? "bg-recessed"
                    : "hover:bg-recessed/60",
                ].join(" ")}
              >
                <Td className="text-text-primary">
                  {row.name}
                </Td>
                <Td className="text-text-secondary">
                  {row.opportunities_competed}
                </Td>
                <Td className="text-text-secondary">
                  {row.wins_against}
                </Td>
                <Td className="text-text-secondary">
                  {row.losses_to}
                </Td>
                <Td className="text-text-secondary">
                  {formatPercent(row.win_rate)}
                </Td>
              </Tr>
            );
          })}
        </TBody>
      </Table>
    </div>
  );
}


function CompetitorSidePanel({
  competitor, opportunities, isLoading, isError, onClose,
}: {
  competitor: CompetitorRow;
  opportunities: CompetitorOpportunityRow[];
  isLoading: boolean;
  isError: boolean;
  onClose: () => void;
}) {
  const matches = useMemo(() => {
    const target = competitor.normalized_name
      || normalizeCompanyName(competitor.name);
    return opportunities.filter((opp) => {
      const firmNormalized = normalizeCompanyName(opp.incumbent_broker_firm);
      // Substring match keeps "Mercer LLC" → "mercer llc" matched
      // against the competitor's "mercer" normalized name without
      // exploding on whitespace differences.
      return firmNormalized === target
        || firmNormalized.startsWith(`${target} `)
        || firmNormalized.endsWith(` ${target}`)
        || firmNormalized.split(" ").includes(target);
    });
  }, [competitor, opportunities]);

  return (
    <aside
      data-testid="competitor-side-panel"
      className="rounded-md border border-border-subtle bg-raised p-3"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-wide text-text-tertiary">
            Opportunities vs.
          </p>
          <p className="text-md font-semibold text-text-primary">
            {competitor.name}
          </p>
          <p className="mt-1 text-xs text-text-secondary">
            {competitor.opportunities_competed} encounter
            {competitor.opportunities_competed === 1 ? "" : "s"}
            {" · "}
            {competitor.wins_against} won
            {" · "}
            {competitor.losses_to} lost
            {competitor.win_rate !== null && (
              <>
                {" · "}{formatPercent(competitor.win_rate)} win rate
              </>
            )}
          </p>
        </div>
        <button
          type="button"
          data-testid="competitor-side-panel-close"
          onClick={onClose}
          className="rounded-md p-1 text-text-tertiary hover:bg-recessed hover:text-text-primary"
          aria-label="Close competitor side panel"
        >
          ×
        </button>
      </header>

      <div className="mt-3">
        {isError ? (
          <DashboardErrorState title="Couldn't load opportunities" />
        ) : isLoading ? (
          <DashboardLoadingState variant="list" rows={2} />
        ) : matches.length === 0 ? (
          <div
            data-testid="competitor-opps-empty"
            className="rounded-md bg-recessed p-3 text-xs text-text-secondary"
          >
            No closed opportunities tagged with this broker as the
            incumbent yet. Encounters tracked above may be on currently
            open pursuits.
          </div>
        ) : (
          <ol className="flex flex-col gap-1.5">
            {matches.map((opp) => (
              <li
                key={opp.id}
                data-testid={`competitor-opp-row-${opp.id}`}
                className="flex items-center justify-between gap-3 rounded-md border border-border-subtle bg-canvas px-3 py-2 text-xs"
              >
                <Link
                  to={`/dashboard/sales-crm/opportunities/${encodeURIComponent(opp.id)}`}
                  className="text-text-primary hover:underline"
                >
                  {opp.name ?? "(unnamed opportunity)"}
                </Link>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={opp.stage === "closed_won" ? "category" : "regulatory"}
                  >
                    {STAGE_LABEL[opp.stage] ?? opp.stage}
                  </Badge>
                  <span className="text-text-tertiary">
                    {formatCents(opp.estimated_annual_revenue_cents)}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>
    </aside>
  );
}
